import * as React from "react";
import { FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Product } from "@/types/product";

interface ExportButtonProps {
  suggestions: Product[];
  query: string;
}

export const ExportButton = ({ suggestions, query }: ExportButtonProps) => {
  const { toast } = useToast();

  const handleExport = (format: 'json' | 'csv') => {
    if (suggestions.length === 0) {
      toast({
        title: "No results to export",
        description: "Please perform a search first.",
        variant: "destructive",
      });
      return;
    }

    let dataStr: string;
    let mimeType: string;
    let fileExtension: string;

    if (format === 'json') {
      dataStr = JSON.stringify(suggestions, null, 2);
      mimeType = 'application/json;charset=utf-8';
      fileExtension = 'json';
    } else {
      const headers = ['objectID', 'title', 'price', 'image_url', 'average_rating', 'rating_amount', 'ean', 'slug'];
      const csvContent = suggestions.map(item => 
        headers.map(header => 
          typeof item[header as keyof Product] === 'string' 
            ? `"${item[header as keyof Product]}"` 
            : item[header as keyof Product]
        ).join(',')
      );
      dataStr = [headers.join(','), ...csvContent].join('\n');
      mimeType = 'text/csv;charset=utf-8';
      fileExtension = 'csv';
    }
    
    const dataUri = `data:${mimeType},${encodeURIComponent(dataStr)}`;
    const exportFileDefaultName = `search-results-${query.replace(/[^a-z0-9]/gi, '-')}-${new Date().toISOString().slice(0, 10)}.${fileExtension}`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
    
    toast({
      title: "Export successful",
      description: `Your search results have been exported as ${format.toUpperCase()}.`,
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="h-12 md:px-4 px-2 gap-1 md:gap-2"
        >
          <FileDown className="h-4 w-4 md:h-5 md:w-5" />
          <span className="hidden md:inline">File</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem onClick={() => handleExport('json')}>
          Export as JSON
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleExport('csv')}>
          Export as CSV
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};